import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  GitCompare, 
  History, 
  FileCode,
  Calendar,
  User,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { VersionDiffViewer, AuditStatusBadge } from '../../../components/smart-contracts';
import { useTemplate, useTemplateVersions, useVersionDiff } from '../../../hooks/useSmartContractTemplates';

export const VersionComparison: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [selectedVersion1, setSelectedVersion1] = useState<string>('');
  const [selectedVersion2, setSelectedVersion2] = useState<string>('');

  const { data: template, isLoading: templateLoading } = useTemplate(id || '');
  const { data: versions, isLoading: versionsLoading } = useTemplateVersions(id || '');
  const { data: diff, isLoading: diffLoading } = useVersionDiff(
    id || '',
    selectedVersion1,
    selectedVersion2
  );

  // Auto-select versions when data loads
  React.useEffect(() => {
    if (versions && versions.length >= 2 && !selectedVersion1 && !selectedVersion2) {
      setSelectedVersion1(versions[1]?.id || '');
      setSelectedVersion2(versions[0]?.id || '');
    }
  }, [versions, selectedVersion1, selectedVersion2]);

  const version1Data = useMemo(() => 
    versions?.find(v => v.id === selectedVersion1),
    [versions, selectedVersion1]
  );

  const version2Data = useMemo(() => 
    versions?.find(v => v.id === selectedVersion2),
    [versions, selectedVersion2]
  );

  if (templateLoading || versionsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-surface-400">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>Template não encontrado</p>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/admin/tokenization/contracts/${id}`)}
            className="p-2 rounded-lg hover:bg-surface-800 text-surface-400 hover:text-surface-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
              <GitCompare className="w-7 h-7 text-primary-400" />
              Comparar Versões
            </h1>
            <p className="text-surface-400 mt-1">
              {template.name} - Compare diferentes versões do código
            </p>
          </div>
        </div>
      </div>

      {/* Version Selectors */}
      <div className="grid grid-cols-2 gap-6">
        {/* Version 1 (Original/Older) */}
        <div className="bg-surface-900 rounded-xl border border-surface-700 p-4">
          <h3 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
            <History className="w-4 h-4" />
            Versão Anterior
          </h3>
          <select
            value={selectedVersion1}
            onChange={(e) => setSelectedVersion1(e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Selecione uma versão</option>
            {versions?.map((v) => (
              <option key={v.id} value={v.id} disabled={v.id === selectedVersion2}>
                v{v.version} - {new Date(v.createdAt).toLocaleDateString('pt-BR')}
              </option>
            ))}
          </select>

          {version1Data && (
            <div className="mt-4 p-3 rounded-lg bg-surface-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Versão:</span>
                <span className="text-sm font-mono text-red-400">v{version1Data.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Data:</span>
                <span className="text-sm text-surface-200 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(version1Data.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Status:</span>
                <AuditStatusBadge status={version1Data.auditStatus} />
              </div>
              {version1Data.changelog && (
                <div className="pt-2 border-t border-surface-700">
                  <span className="text-xs text-surface-500">Changelog:</span>
                  <p className="text-sm text-surface-300 mt-1">{version1Data.changelog}</p>
                </div>
              )}
            </div>
          )}
        </div>


        {/* Version 2 (Modified/Newer) */}
        <div className="bg-surface-900 rounded-xl border border-surface-700 p-4">
          <h3 className="text-sm font-medium text-surface-300 mb-3 flex items-center gap-2">
            <FileCode className="w-4 h-4" />
            Versão Atual
          </h3>
          <select
            value={selectedVersion2}
            onChange={(e) => setSelectedVersion2(e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Selecione uma versão</option>
            {versions?.map((v) => (
              <option key={v.id} value={v.id} disabled={v.id === selectedVersion1}>
                v{v.version} - {new Date(v.createdAt).toLocaleDateString('pt-BR')}
              </option>
            ))}
          </select>

          {version2Data && (
            <div className="mt-4 p-3 rounded-lg bg-surface-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Versão:</span>
                <span className="text-sm font-mono text-green-400">v{version2Data.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Data:</span>
                <span className="text-sm text-surface-200 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(version2Data.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-surface-400">Status:</span>
                <AuditStatusBadge status={version2Data.auditStatus} />
              </div>
              {version2Data.changelog && (
                <div className="pt-2 border-t border-surface-700">
                  <span className="text-xs text-surface-500">Changelog:</span>
                  <p className="text-sm text-surface-300 mt-1">{version2Data.changelog}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Diff Viewer */}
      {selectedVersion1 && selectedVersion2 ? (
        diffLoading ? (
          <div className="flex items-center justify-center h-96 bg-surface-900 rounded-xl border border-surface-700">
            <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          </div>
        ) : version1Data && version2Data ? (
          <VersionDiffViewer
            originalCode={version1Data.sourceCode}
            modifiedCode={version2Data.sourceCode}
            originalVersion={version1Data.version}
            modifiedVersion={version2Data.version}
            diffStats={diff}
            height="600px"
          />
        ) : null
      ) : (
        <div className="flex flex-col items-center justify-center h-96 bg-surface-900 rounded-xl border border-surface-700 text-surface-400">
          <GitCompare className="w-12 h-12 mb-4 opacity-50" />
          <p>Selecione duas versões para comparar</p>
        </div>
      )}

      {/* Changelog Summary */}
      {diff && (
        <div className="bg-surface-900 rounded-xl border border-surface-700 p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">Resumo das Alterações</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="text-2xl font-bold text-green-400">+{diff.additions}</div>
              <div className="text-sm text-green-300">Linhas adicionadas</div>
            </div>
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="text-2xl font-bold text-red-400">-{diff.deletions}</div>
              <div className="text-sm text-red-300">Linhas removidas</div>
            </div>
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="text-2xl font-bold text-blue-400">{diff.changes?.length || 0}</div>
              <div className="text-sm text-blue-300">Total de alterações</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionComparison;
